import React, { ReactPortal, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const ANIMATION_DURATION = 250; // ms

export function Popover({
  anchorEl,
  open,
  children,
  onClose,
}: {
  anchorEl: HTMLElement | null;
  open: boolean;
  children: React.ReactNode;
  onClose: () => void;
}): ReactPortal | null {
  const [style, setStyle] = useState<React.CSSProperties>({});
  const popoverRef = useRef<HTMLUListElement>(null);
  const [show, setShow] = useState(open);
  const [visible, setVisible] = useState(open);

  useEffect(() => {
    if (open && anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      setStyle({
        position: 'absolute',
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        minWidth: rect.width,
        zIndex: 999,
      });
    }
  }, [open, anchorEl]);

  useEffect(() => {
    setShow(open);
    const timeout = setTimeout(() => setVisible(open), ANIMATION_DURATION);

    return () => clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return () => {};
    }

    function handleClick(event: MouseEvent): void {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose();
      }
    }

    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, anchorEl, onClose]);

  if (!show) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        ...style,
        transition: `opacity ${ANIMATION_DURATION}ms cubic-bezier(0.4,0,0.2,1), transform ${ANIMATION_DURATION}ms cubic-bezier(0.4,0,0.2,1)`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-8px)',
      }}
    >
      {React.cloneElement(children as React.ReactElement, { ref: popoverRef })}
    </div>,
    document.body
  );
}
